// Ported from genieos/web/src/utils/axisScaling.ts @ 84923d036
/**
 * Y-axis auto-scaling maths for the historian chart (Demsv1.0#2795).
 *
 * The defect: the historian chart forced a zero baseline, so a grid-frequency
 * trace living in 59.92–60.10 Hz was drawn on a 0–70 axis — about a quarter of
 * one percent of the plot height, roughly a single pixel. Every real deviation
 * was invisible, which is the entire operational value of trending frequency.
 *
 * Fitting tightly fixes that and immediately creates the opposite hazard: a
 * genset holding 60.000 ± 0.002 Hz, fitted to its own range, renders as a
 * violent oscillation. An operator must not have to infer the vertical gain
 * from the tick labels. Two things guard against that here:
 *
 *   1. A per-unit NOISE FLOOR — the fit never gets narrower than a span that
 *      is operationally meaningful for the unit, so a calm signal looks calm.
 *   2. Nice-number bounds plus an explicit step, so the caller can render the
 *      span alongside the axis instead of leaving it to be read off ticks.
 *
 * Everything here is pure: the chart and the on-screen span badge both call
 * these functions on the same visible extent, so the two can never disagree.
 */

export interface AxisBounds {
  min: number;
  max: number;
  /** The nice-number tick step both bounds are exact multiples of. */
  step: number;
}

export interface Extent {
  min: number;
  max: number;
}

/**
 * Absolute minimum span, by unit, for signals whose operating band is a
 * fleet-wide constant rather than a function of magnitude.
 *
 * Keys are lower-cased and trimmed before lookup. A unit absent from this
 * table falls through to the relative floor in minSpanForUnit.
 */
const ABSOLUTE_MIN_SPAN: Record<string, number> = {
  // A 0.2 Hz window is the smallest band in which grid frequency deviation is
  // worth reading; anything tighter is measurement noise presented as signal.
  hz: 0.2,
  // SOC / SOH / load percentages.
  '%': 2,
  pct: 2,
  // Power factor lives in [-1, 1]; 0.05 is the smallest meaningful window.
  pf: 0.05,
};

/**
 * Nominal (reference) value by unit, for the dashed line the chart draws when
 * the fitted band actually contains it.
 *
 * ⚠️ FLEET ASSUMPTION: 60 Hz. Every DJIN site is a 60 Hz North American grid.
 * A 50 Hz deployment must change this entry — it is deliberately the single
 * place that knows, rather than a literal scattered through the chart code.
 */
const NOMINAL_BY_UNIT: Record<string, number> = {
  hz: 60,
  pf: 1,
};

/** Relative noise floor for units with no fleet-wide absolute band: 0.2% of magnitude. */
const RELATIVE_MIN_SPAN_FRACTION = 0.002;

/**
 * Absolute lower bound on the relative floor. A floor AT this value means the
 * relative rule had no magnitude to take a fraction of: the data sits at zero,
 * to within float dust. computeAutoBounds treats that case separately.
 */
const SPAN_EPSILON = 1e-9;

/**
 * Span used for a signal with no spread AND no magnitude: an idle kW, kVAr or
 * A channel reading exactly 0 (djin-cloud#829). Centred on the data, so a
 * flat zero renders on [-1, 1].
 */
const ZERO_SIGNAL_SPAN = 2;

/** Padding added on each side before snapping, as a fraction of the data span. */
const PAD_FRACTION = 0.02;

/** Target number of tick intervals used to pick the nice step. */
const TARGET_INTERVALS = 5;

const normalizeUnit = (unit?: string): string => (unit ?? '').trim().toLowerCase();

/** Strip IEEE-754 dust from a computed bound (0.1 * 3 => 0.30000000000000004). */
const clean = (v: number): number => Number(v.toPrecision(12));

/**
 * Round a raw magnitude up to the nearest 1 / 2 / 5 x 10^n.
 *
 * Degenerate input (zero, negative, NaN) yields the smallest positive step
 * rather than throwing or returning 0, because a zero step would divide-by-zero
 * every caller that snaps against it.
 */
export function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return SPAN_EPSILON;

  const exponent = Math.floor(Math.log10(raw));
  const magnitude = Math.pow(10, exponent);
  const fraction = raw / magnitude;

  let nice: number;
  if (fraction <= 1) nice = 1;
  else if (fraction <= 2) nice = 2;
  else if (fraction <= 5) nice = 5;
  else nice = 10;

  return clean(nice * magnitude);
}

/**
 * The narrowest span the auto-fit is allowed to render for this unit.
 *
 * This is what stops a flat trace being amplified into fake noise. `center` is
 * the midpoint of the data, used only by the relative fallback so that a 3.2 V
 * cell and an 800 V bus each get a sensible floor from one rule.
 */
export function minSpanForUnit(unit: string | undefined, center: number): number {
  const absolute = ABSOLUTE_MIN_SPAN[normalizeUnit(unit)];
  if (absolute !== undefined) return absolute;

  const magnitude = Number.isFinite(center) ? Math.abs(center) : 0;
  return Math.max(magnitude * RELATIVE_MIN_SPAN_FRACTION, SPAN_EPSILON);
}

/**
 * Fit an axis to a data extent: pad, apply the noise floor, and snap both
 * bounds onto a nice-number step.
 *
 * Returns undefined for non-finite input so the caller can leave ECharts on its
 * own default rather than feeding it NaN bounds.
 */
export function computeAutoBounds(
  dataMin: number,
  dataMax: number,
  unit?: string
): AxisBounds | undefined {
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return undefined;

  const lo = Math.min(dataMin, dataMax);
  const hi = Math.max(dataMin, dataMax);
  // Halve before adding, so the midpoint of two large endpoints does not
  // overflow. Belt-and-braces only: minSpanForUnit already treats a non-finite
  // centre as magnitude 0, so this is NOT the route by which extreme input used
  // to produce non-finite bounds (that was the span overflowing and the outward
  // snap exceeding MAX_VALUE — see the guard on the return). Kept because a
  // correct midpoint is cheap and the next reader should not have to re-derive
  // that it was safe.
  const center = lo / 2 + hi / 2;
  const floor = minSpanForUnit(unit, center);

  let min: number;
  let max: number;

  if (hi - lo < floor) {
    // Below the noise floor: widen symmetrically about the data so the trace
    // stays centred and the operator sees a calm signal as calm.
    //
    // Padding rule for a series with no usable spread (djin-cloud#829):
    //   - At a real value v, the unit floor stands. Hz, % and pf use their
    //     operating band (a flat 60 Hz reads 59.9-60.1); other units get a
    //     span of 0.2% of |v| (a flat 500 kW reads 499.4-500.6). This is
    //     deliberately not a wider "v +/- 10%": auto bounds re-fit live while
    //     the operator drags the zoom, so a flat stretch next to a barely-moving
    //     one would swing the gain by one to two orders of magnitude.
    //   - At zero, the relative floor is a fraction of nothing and collapses
    //     to SPAN_EPSILON. That drew an idle kW channel on a 1.2e-9 axis with
    //     ticks at 6e-10. Here, with no scale to fit to, the axis is centre +/- 1,
    //     so a flat zero reads [-1, 1].
    // Only data whose whole spread is below SPAN_EPSILON reaches the zero case.
    // A small signal that swings across zero (e.g. -0.3..+0.3 kVAr) has a
    // centre near 0 but a real span, so it takes the padded branch below.
    const span = floor > SPAN_EPSILON ? floor : ZERO_SIGNAL_SPAN;
    min = center - span / 2;
    max = center + span / 2;
  } else {
    const pad = (hi - lo) * PAD_FRACTION;
    min = lo - pad;
    max = hi + pad;
  }

  const step = niceStep((max - min) / TARGET_INTERVALS);

  // Snap outward. The epsilon absorbs float error so a bound already sitting
  // exactly on a step boundary is not pushed out a whole extra step.
  const snapped = {
    min: clean(Math.floor(min / step + 1e-9) * step),
    max: clean(Math.ceil(max / step - 1e-9) * step),
    step,
  };

  // Snapping outward can only widen, but guard the degenerate case where both
  // bounds land on the same boundary (data exactly on a step, floor ~ 0).
  if (snapped.max <= snapped.min) {
    snapped.min = clean(snapped.min - step);
    snapped.max = clean(snapped.max + step);
  }

  // Last line of defence: extreme finite input can still snap to something
  // non-finite. Returning undefined hands the axis back to ECharts, which is
  // always better than feeding it NaN.
  if (!Number.isFinite(snapped.min) || !Number.isFinite(snapped.max)) return undefined;

  return snapped;
}

/**
 * The nominal value for a unit, if the fleet has one.
 *
 * Undefined means "no opinion" — the chart then draws no reference line, which
 * is strictly better than drawing a wrong one.
 */
export function nominalFor(unit?: string): number | undefined {
  return NOMINAL_BY_UNIT[normalizeUnit(unit)];
}

/**
 * Human-readable span label for the axis badge, e.g. "Hz · span 0.25".
 *
 * The precision tracks the span itself: a 0.25 Hz window needs more decimals
 * than a 730 kW one, and a badge reading "span 0" would defeat its own purpose.
 */
export function formatAxisSpan(extent: Extent, unit?: string): string {
  const span = Math.abs(extent.max - extent.min);
  const label = (unit ?? '').trim();

  // A span can still be far below the fixed-notation resolution: a real
  // micro-scale variation, or a manual pin. (A channel idling at exactly 0
  // used to be the common case, via a ~1e-9 relative floor; computeAutoBounds
  // now gives that a [-1, 1] axis, djin-cloud#829.) Four decimals render such
  // a span as "0.0000", and a badge whose whole job is to state the span must
  // not round its own subject away, so it switches to exponential.
  const value =
    span > 0 && span < 5e-5
      ? span.toExponential(1)
      : span.toFixed(span >= 100 ? 0 : span >= 10 ? 1 : span >= 1 ? 2 : span >= 0.1 ? 3 : 4);

  return label ? `${label} · span ${value}` : `span ${value}`;
}

/**
 * Min/max across every named key of every row, ignoring nulls and non-finite
 * values (a bad decode can produce NaN or Infinity, and one of those would
 * otherwise poison the whole axis).
 *
 * Returns undefined when the axis has no numeric sample at all.
 */
export function extentOf(
  rows: ReadonlyArray<Record<string, number | null | undefined>>,
  keys: ReadonlyArray<string>
): Extent | undefined {
  if (keys.length === 0) return undefined;

  let min = Infinity;
  let max = -Infinity;
  let seen = false;

  for (const row of rows) {
    for (const key of keys) {
      const value = row[key];
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
      seen = true;
    }
  }

  return seen ? { min, max } : undefined;
}
